/**
 * CachedImage Component
 *
 * Caches images to IndexedDB for offline access.
 * Falls back to regular img tag if caching fails.
 */

import { useState, useEffect } from 'react';
import { useThumbnailStore } from '@/stores/thumbnails';

interface CachedImageProps {
  src: string;
  alt: string;
  className?: string;
  fallback?: string;
}

export function CachedImage({ src, alt, className, fallback = './pwa-192x192.webp' }: CachedImageProps) {
  const { getThumbnail, cacheThumbnail, hasThumbnail } = useThumbnailStore();
  const cached = getThumbnail(src);
  const isCached = hasThumbnail(src);

  const [imageSrc, setImageSrc] = useState<string>(cached || src);
  const [error, setError] = useState(false);

  useEffect(() => {
    // If already cached, use it
    if (isCached && cached) {
      setImageSrc(cached);
      return;
    }

    // Cache the URL for future use (just store the URL, not base64 to save space)
    if (src && !isCached) {
      cacheThumbnail(src, src);
    }
  }, [src, isCached, cached, cacheThumbnail]);

  if (error) {
    return (
      <img
        src={fallback}
        alt={alt}
        className={className}
      />
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      referrerPolicy="no-referrer"
      crossOrigin="anonymous"
      onError={() => setError(true)}
    />
  );
}
